'use client';

import React from 'react';
import { cn } from 'utils/styles';
import { VideoPreviewProps, VideoPlayerState } from '../../types';
import { getMergedPreviewClassNames } from '../../utils';
import Image from 'next/image';
import { VIDEO_PLAYER_CONSTANTS } from 'components/VideoPlayer/constants';

const VideoPreview: React.FC<VideoPreviewProps> = ({
  image,
  label,
  showPreviewOnEnd = false,
  priority = false,
  classNames = {},
  videoPlayerState,
}) => {
  if (!videoPlayerState) {
    return null;
  }

  const { playerState, testId, video, handlePlayClick } = videoPlayerState;

  // Determine which states should show the preview
  const previewStates = [VideoPlayerState.Init, VideoPlayerState.Starting];
  if (showPreviewOnEnd) {
    previewStates.push(VideoPlayerState.Ended);
  }

  const showPreview = previewStates.includes(playerState);

  if (!showPreview) {
    return null;
  }

  // Only show preview if image URL is present
  if (!image?.url) {
    return null;
  }

  // Check if this is a portrait image by looking at the image aspect ratio or contentful aspect ratio
  const isPortraitImageSize = () => {
    if (image.width && image.height) return image.height > image.width;
    return video.aspectRatio.aspectRatio === VIDEO_PLAYER_CONSTANTS.PORTRAIT_ASPECT_RATIO;
  };
  const isPortraitImage = isPortraitImageSize();

  // Use label prop if provided, otherwise fall back to video's overlayLabel
  const displayLabel = label ?? video?.overlayLabel;

  const previewClassNames = getMergedPreviewClassNames(classNames);

  // Add opacity transition for starting state
  const wrapperClasses = cn(previewClassNames.wrapper, {
    'opacity-0 pointer-events-none': playerState === VideoPlayerState.Starting,
  });

  const handlePreviewClick = () => {
    if (handlePlayClick) {
      handlePlayClick();
    }
  };

  return (
    <div
      className={wrapperClasses}
      data-testid={`${testId}-preview`}
      onClick={handlePreviewClick}
      role="button"
      tabIndex={0}
      aria-label="Play video"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handlePreviewClick();
        }
      }}>
      {isPortraitImage ? (
        // Portrait image layout: blurry background + contained image on top
        <>
          {/* Blur layer with background image */}
          <div
            className={previewClassNames.blur}
            style={{
              background: `url(${image.url}) center / cover no-repeat`,
            }}
          />
          {/* Contained image overlay that maintains aspect ratio */}
          <Image
            src={image.url}
            className={'absolute inset-0 flex items-center justify-center z-2 object-contain backdrop-blur-sm'}
            alt={''}
            priority={priority}
            fill
          />
          {/* Overlay with gradient and label */}
          <div className={previewClassNames.portraitOverlay} data-testid={`${testId}-overlay`}>
            {displayLabel != null ? (
              <span className={previewClassNames.label} data-testid={`${testId}-overlay-label`}>
                {displayLabel}
              </span>
            ) : null}
          </div>
        </>
      ) : (
        // Landscape image layout: cover the entire video area
        <>
          {/* Background image for landscape images */}
          <Image
            src={image.url}
            className={previewClassNames.image}
            alt={''}
            priority={priority}
            fill
            style={{
              objectFit: 'cover',
            }}
          />
          {/* Overlay with gradient and label */}
          <div className={previewClassNames.overlay} data-testid={`${testId}-overlay`}>
            {displayLabel != null ? (
              <span className={previewClassNames.label} data-testid={`${testId}-overlay-label`}>
                {displayLabel}
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default VideoPreview;
